import { nextRouteStop, type MbmtRouteStop } from './useMbmtApi'
import type { BusRouteStop, BusRouteSummary } from './types'

/**
 * TS mirror of tools/nagpur_route_resolve.py -- keep the two in lockstep; that module's
 * own docstring is the full writeup of WHY this exists (EDMS's schedule_code carries no
 * Up/Down signal at all, and geometric direction-matching was explicitly ruled out as
 * too failure-prone on Nagpur's own data -- see BusRouteMap.tsx's NAGPUR_MAX_COAST_S
 * comment). STATUS_RESOLVED today only ever means a LOOP route (confirmed live
 * 2026-11-04: 20/250 board rows resolved, all 20 LOOP) -- every Up/Down-split route
 * stays ambiguous on purpose rather than guessing a direction.
 */
export const STATUS_RESOLVED = 'resolved'
export const STATUS_UNMAPPED = 'unmapped'
export const STATUS_DEPOT_MISMATCH = 'depot_mismatch'
export const STATUS_AMBIGUOUS_DIRECTION = 'ambiguous_direction'
export const STATUS_AMBIGUOUS_DEPOT_AND_DIRECTION = 'ambiguous_depot_and_direction'
export const STATUS_UNPARSED = 'unparsed_schedule_code'

export interface NagpurRouteResolution {
  status: string
  routeId: string | null
  routeNumber: string | null
  candidates: string[]
}

// Mirrors tools/nagpur_route_resolve.py's _SCHEDULE_CODE_RE exactly -- see that
// module's own comment for why the seq group allows one trailing letter ("04A").
const SCHEDULE_CODE_RE = /^([0-9]+[A-Za-z]*)[A-Z]\/(\d+[A-Za-z]?)\.$/

export function parseRouteNumber(scheduleCode: string | null | undefined): string | null {
  if (!scheduleCode) return null
  const m = SCHEDULE_CODE_RE.exec(scheduleCode.trim())
  return m ? m[1]! : null
}

export function resolveRouteId(
  routeNumber: string,
  homeDepotId: string | null | undefined,
  routes: BusRouteSummary[],
): { status: string; routeId: string | null; candidates: string[] } {
  const allCandidates = routes.filter((r) => r.route_number === routeNumber)
  if (allCandidates.length === 0) {
    return { status: STATUS_UNMAPPED, routeId: null, candidates: [] }
  }

  let pool = allCandidates
  if (homeDepotId) {
    const depotCandidates = allCandidates.filter((r) => r.depot_id === homeDepotId)
    if (depotCandidates.length === 0) {
      return {
        status: STATUS_DEPOT_MISMATCH,
        routeId: null,
        candidates: allCandidates.map((r) => r.route_id),
      }
    }
    pool = depotCandidates
  }

  if (pool.length === 1) {
    return { status: STATUS_RESOLVED, routeId: pool[0]!.route_id, candidates: [pool[0]!.route_id] }
  }
  const status = homeDepotId ? STATUS_AMBIGUOUS_DIRECTION : STATUS_AMBIGUOUS_DEPOT_AND_DIRECTION
  return { status, routeId: null, candidates: pool.map((r) => r.route_id) }
}

export function resolveVehicle(
  scheduleCode: string | null | undefined,
  homeDepotId: string | null | undefined,
  routes: BusRouteSummary[],
): NagpurRouteResolution {
  const routeNumber = parseRouteNumber(scheduleCode)
  if (routeNumber === null) {
    return { status: STATUS_UNPARSED, routeId: null, routeNumber: null, candidates: [] }
  }
  return { ...resolveRouteId(routeNumber, homeDepotId, routes), routeNumber }
}

/** Adapts a resolved route's own (already direction-unambiguous) stop list into
 *  nextRouteStop()'s MbmtRouteStop shape -- same nearest/ahead-behind geometry MBMT's
 *  map already uses, reused rather than reimplemented. The constant `direction` value
 *  is a trivial single-leg stand-in: nextRouteStop's own leg-scoping filter
 *  (`usable.filter(s => s.direction === nearest.direction)`) only matters when two
 *  different legs are merged into one list, which never happens here -- a resolved
 *  route_id already IS one leg. */
function toMbmtStop(s: BusRouteStop): MbmtRouteStop {
  return {
    direction: 'resolved', seq: s.sequence_no, source: 'bus_route_stop',
    stage_id: null, name: s.stop_name, lat: s.lat, lon: s.lon, along: null,
  }
}

export function nextBusRouteStop(
  stops: BusRouteStop[], pos: { lat: number; lon: number }, opts?: { moving?: boolean },
): BusRouteStop | null {
  const next = nextRouteStop(stops.map(toMbmtStop), pos, opts)
  if (!next) return null
  return stops.find((s) => s.sequence_no === next.seq) ?? null
}
